import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import Button from '../Button/Button';
import Icons from '../../assets/index';
import { trackEvent } from '@/utils/track-event';
import { PostHogEvents } from '@/utils/PostHogEvents';
import { getAuthData } from '@/utils/auth';
import { useSelector } from 'react-redux';

const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
type Mode = 'draw' | 'upload';

interface SignatureModalProps {
  onClose: () => void;
  onSignatureSubmit?: (signatureData: {
    type: string;
    value: string | null;
    file?: File | null;
    canvas?: any;
  }) => void;
  initialSignature?: {
    type: string;
    value: string | null;
    file?: File | null;
  } | null;
}

const SignatureModal = ({ onClose, onSignatureSubmit, initialSignature }: SignatureModalProps) => {
  const { email } = getAuthData();
  const sectionId = useSelector((state: any) => state.sectionId.sectionId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sigCanvas = useRef<any>(null);
  const [mode, setMode] = useState<Mode>(initialSignature?.type === 'uploaded' ? 'upload' : 'draw');
  const [uploadedImage, setUploadedImage] = useState<string | null>(
    initialSignature?.type === 'uploaded' ? initialSignature.value : null
  );
  const [isDragging, setIsDragging] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);
  const [savedDrawing, setSavedDrawing] = useState<string | null>(
    initialSignature?.type === 'drawn' ? initialSignature.value : null
  );

  const [error, setError] = useState('');
  const [file, setFile] = useState<File | null>(initialSignature?.file || null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState<'draw' | 'upload' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [canvasKey, setCanvasKey] = useState(0);
  const [forceUpdate, setForceUpdate] = useState(0);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newDimensions = {
          width: Math.floor(rect.width),
          height: Math.floor(rect.height),
        };

        if (newDimensions.width > 0 && newDimensions.height > 0) {
          setDimensions((prev) => {
            if (prev.width !== newDimensions.width || prev.height !== newDimensions.height) {
              return newDimensions;
            }
            return prev;
          });
        }
      }
    };

    const initDimensions = () => {
      let attempts = 0;
      const maxAttempts = 10;

      const tryUpdate = () => {
        updateDimensions();
        attempts++;

        if (containerRef.current && attempts < maxAttempts) {
          const rect = containerRef.current.getBoundingClientRect();
          if (rect.width === 0 || rect.height === 0) {
            setTimeout(tryUpdate, 50);
            return;
          }
        }
      };

      tryUpdate();
    };

    initDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, [mode]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCanvasReady(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setIsSubmitted(false);
    setError('');
  }, []);

  useEffect(() => {
    if (
      mode !== 'draw' ||
      !savedDrawing ||
      !canvasReady ||
      dimensions.width === 0 ||
      dimensions.height === 0
    )
      return;

    // Make sure we have a delay to ensure canvas is fully initialized
    const timer = setTimeout(() => {
      if (
        sigCanvas.current &&
        sigCanvas.current.getCanvas &&
        typeof sigCanvas.current.getCanvas === 'function'
      ) {
        try {
          const canvas = sigCanvas.current.getCanvas();
          if (!canvas || canvas.width === 0 || canvas.height === 0) return;

          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) return;

          const img = new Image();
          img.onload = () => {
            try {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            } catch (err) {
              console.error('Error drawing on canvas:', err);
            }
          };
          img.onerror = (err) => {
            console.error('Error loading saved image:', err);
          };
          img.src = savedDrawing;
        } catch (err) {
          console.error('Error loading saved drawing:', err);
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [canvasReady, mode, savedDrawing, dimensions]);

  useEffect(() => {
    if (mode === 'draw') {
      if (
        canvasReady &&
        sigCanvas.current &&
        typeof sigCanvas.current.clear === 'function' &&
        !savedDrawing &&
        dimensions.width > 0 &&
        dimensions.height > 0
      ) {
        setTimeout(() => {
          if (sigCanvas.current && typeof sigCanvas.current.clear === 'function') {
            sigCanvas.current.clear();
          }
        }, 100);
      }
    }
  }, [mode, canvasReady, savedDrawing, dimensions, canvasKey]);

  const validateFile = (fileToValidate: File) => {
    // Check file type
    const fileExtension = fileToValidate.name
      .toLowerCase()
      .slice(fileToValidate.name.lastIndexOf('.'));
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      setError(
        'Invalid file type. Please upload a valid file in .jpg, .jpeg or .png format, with maximum size of 1 MB.'
      );
      return false;
    }
    // Check file size
    if (fileToValidate.size > 1 * 1024 * 1024) {
      setError(`File size too large. Please upload a file smaller than 1 MB.`);
      return false;
    }

    setError('');
    return true;
  };

  const isCanvasEmpty = () => {
    try {
      if (!sigCanvas.current) return true;
      if (!sigCanvas.current.getCanvas) return true;

      const canvas = sigCanvas.current.getCanvas();
      if (!canvas) return true;

      if (canvas.width === 0 || canvas.height === 0) return true;

      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return true;

      try {
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const pixelBuffer = new Uint32Array(imageData.data.buffer);
        return !pixelBuffer.some((color) => color !== 0);
      } catch (err) {
        return !savedDrawing;
      }
    } catch (err) {
      console.error('Error checking if canvas is empty:', err);
      return !savedDrawing;
    }
  };

  const getCanvasDataURL = () => {
    try {
      if (!sigCanvas.current) return null;
      if (typeof sigCanvas.current.toDataURL !== 'function') return null;

      return sigCanvas.current.toDataURL('image/png');
    } catch (err) {
      console.error('Error getting canvas data URL:', err);
      return null;
    }
  };

  const clearCanvas = () => {
    try {
      if (sigCanvas.current && typeof sigCanvas.current.clear === 'function') {
        sigCanvas.current.clear();
        setSavedDrawing(null);
        setError('');
      }
    } catch (err) {
      console.error('Error clearing canvas:', err);
    }
  };

  // Handlers for Upload Drag and Drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (!droppedFile) return;

    if ((file || uploadedImage || savedDrawing) && !isSubmitted) {
      if (validateFile(droppedFile)) {
        setShowReplaceConfirm(true);
        setPendingAction('upload');
        if (fileInputRef.current) {
          (fileInputRef.current as any).pendingFile = droppedFile;
        }
      }
      return;
    }

    if (validateFile(droppedFile)) {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedImage(reader.result as string);
        setFile(droppedFile);
        setError('');
      };
      reader.readAsDataURL(droppedFile);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setError('');
    setUploadedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if ((file || uploadedImage || savedDrawing) && !isSubmitted) {
      if (validateFile(selectedFile)) {
        setShowReplaceConfirm(true);
        setPendingAction('upload');
        (e.target as any).pendingFile = selectedFile;
      }
      return;
    }

    if (validateFile(selectedFile)) {
      setFile(selectedFile);
      setUploadedImage(URL.createObjectURL(selectedFile));
      setError('');
    } else {
      setFile(null);
      e.target.value = '';
    }
  };

  const handleSubmit = () => {
    if (!onSignatureSubmit) {
      onClose();
      return;
    }

    setError('');

    switch (mode) {
      case 'draw': {
        const hasSavedSignature = savedDrawing !== null;
        let hasDrawnSignature = false;

        if (dimensions.width > 0 && dimensions.height > 0) {
          hasDrawnSignature = !isCanvasEmpty();
        }

        if (!hasDrawnSignature && !hasSavedSignature) {
          setError('Please draw your signature before submitting');
          return;
        }

        const dataURL = savedDrawing || getCanvasDataURL();
        if (!dataURL) {
          setError('Failed to capture signature. Please try again.');
          return;
        }

        // Save the drawing for later use
        setSavedDrawing(dataURL);
        setIsSubmitted(true);

        onSignatureSubmit({
          type: 'drawn',
          value: dataURL,
          canvas: sigCanvas.current,
        });
        break;
      }

      case 'upload': {
        if (!file || !uploadedImage) {
          setError('Please upload a signature image before submitting');
          return;
        }

        setIsSubmitted(true);
        onSignatureSubmit({
          type: 'uploaded',
          value: uploadedImage,
          file: file,
        });
        break;
      }
    }

    trackEvent(PostHogEvents.FORM_SUBMITTED, {
      form_id: sectionId,
      user_id: email,
      timestamp: new Date().toISOString(),
    });

    onClose();
  };

  const handleBeginDrawing = () => {
    if (savedDrawing && isSubmitted) {
      setShowReplaceConfirm(true);
      setPendingAction('draw');
      return false;
    }
    return true;
  };

  const handleEndDrawing = () => {
    try {
      setTimeout(() => {
        if (sigCanvas.current && sigCanvas.current.getCanvas) {
          const canvas = sigCanvas.current.getCanvas();
          if (canvas && !isCanvasEmpty()) {
            const dataURL = getCanvasDataURL();
            if (dataURL) {
              setSavedDrawing(dataURL);
            }
            if (error && mode === 'draw') {
              setError('');
            }
          }
        }
      }, 10);
    } catch (err) {
      console.error('Error saving drawing:', err);
    }
  };

  const handleModeChange = (newMode: Mode) => {
    const hasExistingDrawing = savedDrawing && mode === 'draw';
    const hasExistingUpload = (file || uploadedImage) && mode === 'upload';

    if (hasExistingDrawing || hasExistingUpload) {
      setShowReplaceConfirm(true);
      setPendingAction(newMode);
      return;
    }

    performModeChange(newMode);
  };

  const performModeChange = (newMode: Mode) => {
    if (newMode === 'draw') {
      setFile(null);
      setUploadedImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      setSavedDrawing(null);
      setCanvasReady(false);

      setDimensions({ width: 0, height: 0 });

      setCanvasKey((prev) => prev + 1);
      setForceUpdate((prev) => prev + 1);

      if (sigCanvas.current && typeof sigCanvas.current.clear === 'function') {
        sigCanvas.current.clear();
      }
    } else if (newMode === 'upload') {
      setSavedDrawing(null);
      if (sigCanvas?.current && typeof sigCanvas?.current.clear === 'function') {
        sigCanvas.current.clear();
      }
    }

    setMode(newMode);
    setIsSubmitted(false);
    setError('');

    if (newMode === 'draw') {
      setTimeout(() => {
        setCanvasReady(true);
      }, 100);
    }
  };

  const handleReplaceConfirm = (confirm: boolean) => {
    if (confirm && pendingAction) {
      if (pendingAction === 'upload' && fileInputRef.current) {
        const pendingFile = (fileInputRef.current as any).pendingFile;
        if (pendingFile && validateFile(pendingFile)) {
          setFile(pendingFile);
          setUploadedImage(URL.createObjectURL(pendingFile));
          setError('');
          setSavedDrawing(null);
        }
        // Clean up
        (fileInputRef.current as any).pendingFile = null;
      } else {
        performModeChange(pendingAction);
      }
    } else {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
        (fileInputRef.current as any).pendingFile = null;
      }
    }
    setShowReplaceConfirm(false);
    setPendingAction(null);
  };

  const renderDraw = () => (
    <div className="flex flex-col w-full h-full">
      <div className="p-2 font-semibold text-sm text-ds-gray-500">Draw</div>
      <div className="flex-1 flex w-full justify-center items-center border border-dashed border-[#CACACA] bg-ds-gray-100 rounded relative">
        <div
          ref={containerRef}
          className="w-full h-full"
          key={`container-${canvasKey}-${forceUpdate}`}
        >
          {canvasReady && dimensions.width > 0 && dimensions.height > 0 && (
            <SignatureCanvas
              key={`canvas-${canvasKey}-${dimensions.width}-${dimensions.height}`}
              penColor="black"
              canvasProps={{
                className: 'rounded cursor-crosshair',
                width: dimensions.width,
                height: dimensions.height,
                style: {
                  width: `${dimensions.width}px`,
                  height: `${dimensions.height}px`,
                  touchAction: 'none',
                  display: 'block',
                },
              }}
              ref={sigCanvas}
              onBegin={handleBeginDrawing}
              onEnd={handleEndDrawing}
              clearOnResize={false}
            />
          )}

          {(!canvasReady || dimensions.width === 0 || dimensions.height === 0) && (
            <div className="absolute inset-0 flex items-center justify-center bg-ds-gray-100 bg-opacity-90 rounded">
              <div className="flex flex-col items-center space-y-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ds-purple-600"></div>
                <span className="text-sm text-ds-gray-500">Loading canvas...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="py-2 flex justify-between w-full">
        <div className="w-1/2">
          {error && mode === 'draw' && (
            <div className="py-2">
              <p className="text-xs text-ds-red-500 font-normal">{error}</p>
            </div>
          )}
        </div>
        <div className="w-1/2 text-right">
          <button onClick={clearCanvas} className="text-xs text-ds-purple-600 underline mr-4">
            Clear
          </button>
        </div>
      </div>
    </div>
  );

  // upload
  const renderUpload = () => (
    <div className="flex h-full w-full flex-grow flex-col justify-center items-center">
      <div className="self-start p-2 font-semibold text-sm text-ds-gray-500">Upload Signature</div>
      <div
        className={`border border-dashed p-6 bg-ds-gray-50 text-center cursor-pointer rounded-md transition-colors duration-200 h-full w-full 
          ${isDragging ? 'bg-blue-50' : ''}
          ${file ? 'bg-ds-gray-100' : 'bg-ds-gray-50'}
          ${error && mode === 'upload' ? 'border-ds-red-500' : 'border-[#CACACA]'}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept=".jpg, .jpeg, .png, image/*"
          onChange={handleFileChange}
        />
        {!uploadedImage ? (
          <div className="flex items-center justify-center h-full w-full">
            <div className="flex flex-col items-center justify-center text-ds-gray-500 text-center px-4">
              <Icons.signUploadIcon />
              <p className="text-sm font-normal">Click or drag a file to this area to upload</p>
              <p className="text-xs font-normal">
                (Accepted file types: .jpg, .png, .jpeg. Maximum file size: 1 MB.)
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-[100px]">
            <img
              src={uploadedImage}
              alt="Signature preview"
              className="h-full w-full object-contain"
            />
          </div>
        )}
      </div>
      {file && (
        <div className="border border-ds-gray-200 rounded-sm my-2 w-full">
          <div className="flex p-2 items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gray-2500">
                <Icons.signatureIcon />
              </div>
              <div>
                <h2 className="text-ds-gray-600 text-lg font-medium truncate max-w-xs">
                  {file?.name}
                </h2>
                <p className="text-sm text-gray-300 font-normal">
                  {(file.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
            </div>
            <button onClick={handleRemoveFile} className="px-2">
              <Icons.trashIcon />
            </button>
          </div>
        </div>
      )}
      {error && mode === 'upload' && (
        <div className="py-2 self-start">
          <p className="text-xs text-ds-red-500 font-normal pl-2">{error}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="relative bg-ds-gray-gray-white p-6 w-[60vw] h-[80vh] rounded-lg shadow-lg flex flex-col">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-center text-2xl font-normal text-ds-gray-800">
            Form Filler Signature
          </h1>
          <p className="text-sm font-normal text-ds-gray-900 text-center max-w-[90%] mx-auto mt-2">
            Choose a signature method: Draw or Upload and add the signature.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex mb-4 rounded">
          {['draw', 'upload'].map((tab) => (
            <button
              key={tab}
              onClick={() => handleModeChange(tab as Mode)}
              className={`flex-1 p-2 capitalize ${
                mode === tab
                  ? 'bg-ds-mc-blue-600 font-semibold text-ds-gray-gray-white rounded'
                  : 'bg-ds-gray-50 font-semibold text-ds-gray-500 text-sm'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto min-h-0 pb-14">
          {mode === 'draw' && renderDraw()}
          {mode === 'upload' && renderUpload()}
        </div>

        {/* Sticky Footer */}
        <div className="absolute bottom-0 left-0 w-full bg-ds-gray-gray-white py-2 px-4 rounded-b-lg">
          <div className="flex justify-end gap-4">
            <Button onClick={handleSubmit} color="primary">
              Submit
            </Button>
            <Button onClick={onClose} color="outlined">
              Cancel
            </Button>
          </div>
        </div>
      </div>

      {showReplaceConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-60">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Replace Signature?</h2>
            <p className="text-gray-600 mb-6">
              You already have a signature. Switching to {pendingAction} will replace your current
              signature. Are you sure you want to continue?
            </p>
            <div className="flex justify-end gap-3">
              <Button onClick={() => handleReplaceConfirm(false)} color="outlined" size="sm">
                Cancel
              </Button>
              <Button onClick={() => handleReplaceConfirm(true)} color="primary" size="sm">
                Replace
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureModal;
